import { matchPath } from 'react-router'

import { localStorageKeys } from '@sketch/constants'
import { WorkspaceMinimalFragment } from '@sketch/gql-types'
import { getItem } from '@sketch/utils'

import {
  getCommandBarItemScore,
  normalizeCommandBarSearchText,
} from './commandSearch'
import { CommandBarItem, CommandBarSection } from '../types'
import { routes } from '../../routes'

const commandBarSectionOrder: CommandBarSection[] = [
  'Inspect',
  'Current Document',
  'Current Folder',
  'Current View',
  'Workspace',
  'Workspace Settings',
  'Account Settings',
  'Appearance',
  'Other Workspaces',
  'Documents',
  'Folders',
]

const getSectionOrder = (section: CommandBarSection) =>
  commandBarSectionOrder.indexOf(section)

export const getCurrentWorkspace = (
  pathname: string,
  workspaces: WorkspaceMinimalFragment[]
) => {
  const match = matchPath<{ workspaceId: string }>(pathname, {
    path: routes.WORKSPACE.template(),
  })
  const workspaceIdFromPath = match?.params.workspaceId
  const lastWorkspaceId = getItem(localStorageKeys.lastWorkspaceIdKey)
  const workspaceId = workspaceIdFromPath || lastWorkspaceId

  if (workspaceId) {
    return workspaces.find(
      workspace =>
        workspace.identifier.toLowerCase() === workspaceId.toLowerCase()
    )
  }

  return workspaces[0]
}

export const areCommandBarCommandsEqual = (
  commandA: CommandBarItem,
  commandB: CommandBarItem
) =>
  commandA.id === commandB.id &&
  commandA.analyticsId === commandB.analyticsId &&
  commandA.kind === commandB.kind &&
  commandA.title === commandB.title &&
  commandA.subtitle === commandB.subtitle &&
  commandA.section === commandB.section &&
  commandA.shortcut === commandB.shortcut &&
  commandA.disabledReason === commandB.disabledReason &&
  commandA.icon === commandB.icon &&
  commandA.iconNode === commandB.iconNode &&
  commandA.priority === commandB.priority &&
  (commandA.keywords || []).join('|') === (commandB.keywords || []).join('|')

export const areCommandBarCommandGroupsEqual = (
  commandsA: CommandBarItem[],
  commandsB: CommandBarItem[]
) =>
  commandsA.length === commandsB.length &&
  commandsA.every((command, index) =>
    areCommandBarCommandsEqual(command, commandsB[index])
  )

const getSectionScore = (items: CommandBarItem[], query: string) =>
  Math.max(...items.map(item => getCommandBarItemScore(item, query)))

export const getCommandBarRenderState = (
  items: CommandBarItem[],
  query: string
) => {
  const groupedItems = new Map<CommandBarSection, CommandBarItem[]>()
  const shouldRankSectionsByMatch = normalizeCommandBarSearchText(query) !== ''

  items.forEach(item => {
    const sectionItems = groupedItems.get(item.section) || []
    sectionItems.push(item)
    groupedItems.set(item.section, sectionItems)
  })

  const sections = Array.from(groupedItems.entries()).sort(
    ([sectionA, itemsA], [sectionB, itemsB]) => {
      if (shouldRankSectionsByMatch) {
        const scoreDifference =
          getSectionScore(itemsB, query) - getSectionScore(itemsA, query)

        if (scoreDifference !== 0) {
          return scoreDifference
        }
      }

      return getSectionOrder(sectionA) - getSectionOrder(sectionB)
    }
  )
  const renderedItems = sections.flatMap(([, sectionItems]) => sectionItems)

  return {
    renderedItemIds: renderedItems.map(item => item.id).join('|'),
    renderedItems,
    sections,
  }
}
